import { useEffect, useState } from 'react'

import localStorage from '../../utils/localStorage'

import './style.css'

// After "Not now", wait this long before offering again
const SNOOZE_DAYS = 60
const DISMISSED_KEY = 'installAppBannerDismissedAt'

const isPhone = () => window.matchMedia('(max-width: 767px)').matches

// Already opened from the home screen
const isInstalled = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true

// iPads report themselves as Macs, so also check for a touch screen
const isIOS = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
  (window.navigator.platform === 'MacIntel' &&
    window.navigator.maxTouchPoints > 1 &&
    !/android/i.test(window.navigator.userAgent))

const isSnoozed = () => {
  try {
    const dismissedAt = Number(localStorage.getItem(DISMISSED_KEY))
    return dismissedAt > 0 && Date.now() - dismissedAt < SNOOZE_DAYS * 24 * 60 * 60 * 1000
  } catch (e) {
    return false
  }
}

const ShareIcon = () => (
  <svg className="installAppShareIcon" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12 3v12M7.5 7.5 12 3l4.5 4.5M7 11H5.5A1.5 1.5 0 0 0 4 12.5v7A1.5 1.5 0 0 0 5.5 21h13a1.5 1.5 0 0 0 1.5-1.5v-7a1.5 1.5 0 0 0-1.5-1.5H17"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const InstallAppBanner = () => {
  const [installPrompt, setInstallPrompt] = useState(() => window.morselInstallPrompt || null)
  const [hidden, setHidden] = useState(() => !isPhone() || isInstalled() || isSnoozed())

  useEffect(() => {
    const onAvailable = () => setInstallPrompt(window.morselInstallPrompt || null)
    const onInstalled = () => setHidden(true)
    window.addEventListener('morsel-install-available', onAvailable)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('morsel-install-available', onAvailable)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  const ios = isIOS()

  // Android shows our banner only once Chrome says it can install;
  // other browsers that can't install get nothing.
  if (hidden || (!ios && !installPrompt)) {
    return null
  }

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, String(Date.now()))
    } catch (e) {
      // Browser storage is blocked (e.g. private mode): hide it for this visit only
    }
    setHidden(true)
  }

  const install = async () => {
    installPrompt.prompt()
    const { outcome } = await installPrompt.userChoice
    window.morselInstallPrompt = null
    setInstallPrompt(null)
    if (outcome !== 'accepted') {
      dismiss()
    }
  }

  return (
    <div className="installAppBanner" role="region" aria-label="Add Morsel to your phone">
      <img className="installAppIcon" src="/apple-touch-icon.png" alt="" />
      <div className="installAppText">
        <strong>Add Morsel to your phone</strong>
        {ios ? (
          <span>
            Tap <ShareIcon /> <b>Share</b>, then <b>Add to Home Screen</b>. You'll stay signed in.
          </span>
        ) : (
          <span>Open your recipes in one tap. You'll stay signed in.</span>
        )}
        {!ios && (
          <button type="button" className="installAppButton" onClick={install}>
            Add to home screen
          </button>
        )}
      </div>
      <button type="button" className="installAppClose" onClick={dismiss} aria-label="Not now">
        ×
      </button>
    </div>
  )
}

export default InstallAppBanner
